import React from "react";
import {
  Controller,
  useFieldArray,
  type Control,
  type FieldErrors,
} from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  Autocomplete,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import timezones from "./timezone.json";

interface Shift {
  start: string;
  end: string;
}

export interface PlantFormData {
  id?: string;
  name: string;
  displayName: string;
  description: string;
  autoClosureTime: string;
  shifts: Shift[];
  lines: string;
  timezone: string;
}

interface PlantFormProps {
  control: Control<PlantFormData>;
  errors: FieldErrors<PlantFormData>;
  isLoading: boolean;
  formErrors?: Record<string, string>;
}

const PlantForm: React.FC<PlantFormProps> = ({
  control,
  errors,
  isLoading,
  formErrors = {},
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "shifts",
  });

  const getErrorMessage = (
    fieldName: string,
    fieldError?: { message?: string }
  ) => {
    return fieldError?.message || formErrors[fieldName];
  };

  return (
    <Box>
      {/* Form-level errors */}
      {Object.keys(formErrors).length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "#ffebee",
            borderRadius: 1,
            border: "1px solid #f44336",
          }}
        >
          <Typography
            variant="body2"
            color="error"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Please fix the following errors:
          </Typography>
          {Object.entries(formErrors).map(([field, message]) => (
            <Typography
              key={field}
              variant="caption"
              color="error"
              sx={{ display: "block" }}
            >
              • {message}
            </Typography>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        {/* Plant Code (legacy, kept for DB compatibility) */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "var(--Text-color_text_secondary, #626F86)",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Plant Code
          </Typography>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Plant Code (eg. PLT01)"
                  error={!!error || !!formErrors.code}
                  disabled={isLoading}
                  onBlur={field.onBlur}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      display: "flex",
                      height: "40px",
                      padding: "0 12px",
                      alignItems: "center",
                      gap: "6px",
                      borderRadius: "8px",
                      border: "1px solid rgba(9, 30, 66, 0.14)",
                      background: "rgba(9, 30, 66, 0.06)",
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
                {(error || formErrors.code) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {getErrorMessage("code", error)}
                  </Typography>
                )}
              </>
            )}
          />
        </Box>

        {/* Plant Display Name (new, human-friendly) */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "var(--Text-color_text_secondary, #626F86)",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Plant Name
          </Typography>
          <Controller
            name="displayName"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Plant Display Name (eg. OTA , Kaduna)"
                  error={!!error || !!formErrors.displayName}
                  disabled={isLoading}
                  onBlur={field.onBlur}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      display: "flex",
                      height: "40px",
                      padding: "0 12px",
                      alignItems: "center",
                      gap: "6px",
                      borderRadius: "8px",
                      border: "1px solid rgba(9, 30, 66, 0.14)",
                      background: "rgba(9, 30, 66, 0.06)",
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
                {(error || formErrors.displayName) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {getErrorMessage("displayName", error)}
                  </Typography>
                )}
              </>
            )}
          />
        </Box>
      </Box>
      {/* Plant Description */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2, 
          mb: 3,
        }}
      >
        {/* Plant Description */}
      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" } }}>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: "var(--Text-color_text_secondary, #626F86)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Plant Description
        </Typography>
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="Plant Description - Location"
                error={!!error || !!formErrors.description}
                disabled={isLoading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    padding: "0 12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(9, 30, 66, 0.14)",
                    background: "rgba(9, 30, 66, 0.06)",
                    "& fieldset": { border: "none" },
                  },
                }}
              />
              {(error || formErrors.description) && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {getErrorMessage("description", error)}
                </Typography>
              )}
            </>
          )}
        />
        </Box>

        {/* Timezone */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "var(--Text-color_text_secondary, #626F86)",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Timezone
          </Typography>

          <Controller
            name="timezone"
            control={control}
            render={({ fieldState: { error } }) => (
              <>
            <FormControl
              // size="small"
              fullWidth
              disabled={isLoading}
              error={!!error || !!formErrors.lines}
            >               
            <Controller
              name="timezone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <FormControl fullWidth >
                    <Autocomplete
                      options={timezones}
                      value={field.value ?? null}
                      onChange={(_, newValue) => {
                        field.onChange(newValue);
                        field.onBlur();  
                      }}
                      onBlur={field.onBlur}
                      disabled={isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Timezone"
                          error={!!error || !!formErrors.timezone}
                          sx={{
                            // background: "rgba(9, 30, 66, 0.06)",
                            // borderRadius: "8px",
                            "& .MuiOutlinedInput-root": {
                                height: "40px",
                                padding: "0 12px",
                                borderRadius: "8px",
                                border: "1px solid rgba(9, 30, 66, 0.14)",
                                background: "rgba(9, 30, 66, 0.06)",
                                "& fieldset": { border: "none" },
                            }
                          }}
                        />
                      )}
                    />
                  </FormControl>

                  {(error || formErrors.timezone) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >

                      {getErrorMessage("timezone", error)==='Invalid input: expected string, received null' ? 'Please select a valid Time zone' : getErrorMessage("timezone", error)}
                    </Typography>
                  )}
                </>
              )}
            />
                </FormControl>

              </>
            )}
          />
        </Box>
      </Box>

      {/* Auto Closure Time - MM:SS format */}
      <Box sx={{ mb: 3, width: { xs: "100%", sm: "25%" } }}>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: "var(--Text-color_text_secondary, #626F86)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Auto Closure Time Stoppage (MM:SS)
        </Typography>
        <Controller
          name="autoClosureTime"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="00:00"
                error={!!error || !!formErrors.autoClosureTime}
                disabled={isLoading}
                onBlur={field.onBlur}
                value={field.value || ""}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9:]/g, "");

                  // Remove existing colons
                  value = value.replace(/:/g, "");

                  // Format as MM:SS
                  if (value.length >= 2) {
                    const minutes = value.slice(0, 2);
                    const seconds = value.slice(2, 4);

                    // Validate minutes (00-59)
                    const mins = parseInt(minutes, 10);
                    if (mins > 59) {
                      value = "59" + seconds;
                    } else {
                      value = minutes + (seconds ? ":" + seconds : "");
                    }

                    // Validate seconds (00-59)
                    if (seconds) {
                      const secs = parseInt(seconds, 10);
                      if (secs > 59) {
                        value = minutes + ":59";
                      }
                    }
                  }

                  // Limit to MM:SS format
                  if (value.length > 5) {
                    value = value.slice(0, 5);
                  }

                  field.onChange(value);
                }}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter
                  if (
                    [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
                    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                    (e.keyCode === 65 && e.ctrlKey === true) ||
                    (e.keyCode === 67 && e.ctrlKey === true) ||
                    (e.keyCode === 86 && e.ctrlKey === true) ||
                    (e.keyCode === 88 && e.ctrlKey === true) ||
                    // Allow: home, end, left, right
                    (e.keyCode >= 35 && e.keyCode <= 39)
                  ) {
                    return;
                  }
                  // Ensure that it is a number or colon
                  if (
                    (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
                    (e.keyCode < 96 || e.keyCode > 105) &&
                    e.keyCode !== 186 &&
                    e.keyCode !== 59
                  ) {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  maxLength: 5,
                  pattern: "[0-9:]*",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    display: "flex",
                    height: "40px",
                    padding: "0 12px",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "8px",
                    border: "1px solid rgba(9, 30, 66, 0.14)",
                    background: "rgba(9, 30, 66, 0.06)",
                    "& fieldset": { border: "none" },
                  },
                }}
              />
              {(error || formErrors.autoClosureTime) && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {getErrorMessage("autoClosureTime", error)}
                </Typography>
              )}
            </>
          )}
        />
      </Box>

      {/* Shift Timing */}
      <Box sx={{ mb: 3, width: "100%" }}>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: "var(--Text-color_text_secondary, #626F86)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Shift Timing (24 Hrs Format)
        </Typography>
        {(errors.shifts?.message || formErrors.shifts) && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 1, display: "block" }}
          >
            {getErrorMessage("shifts", errors.shifts as any)}
          </Typography>
        )}

        {fields.map((field, index) => {
          const getAmPm = (time: string) => {
            if (!time) return "";
            const [hours] = time.split(":").map(Number);
            return hours >= 12 ? "PM" : "AM";
          };

          return (
            <Box
              key={field.id}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mb: 2,
                alignItems: "flex-start",
                width: { xs: "100%", sm: "50%" },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: "var(--Text-color_text_secondary, #626F86)",
                  fontSize: 14,
                  fontWeight: 500,
                  minWidth: { xs: "auto", sm: "60px" },
                  mt: 1,
                }}
              >
                Shift {index + 1}
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                {/* Start Time */}
                <Controller
                  name={`shifts.${index}.start`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box sx={{ flex: 1, position: "relative" }}>
                      <TextField
                        {...field}
                        size="small"
                        type="time"
                        error={!!error || !!formErrors[`shifts.${index}.start`]}
                        disabled={isLoading}
                        onBlur={field.onBlur}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            display: "flex",
                            height: "40px",
                            padding: "0 12px",
                            alignItems: "center",
                            gap: "6px",
                            borderRadius: "8px",
                            border: "1px solid rgba(9, 30, 66, 0.14)",
                            background: "rgba(9, 30, 66, 0.06)",
                            "& fieldset": { border: "none" },
                          },
                        }}
                      />
                      {field.value && (
                        <Typography
                          variant="body2"
                          sx={{
                            position: "absolute",
                            top: "20px",
                            right: 8,
                            transform: "translateY(-50%)",
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {getAmPm(field.value)}
                        </Typography>
                      )}
                      {(error || formErrors[`shifts.${index}.start`]) && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {getErrorMessage(`shifts.${index}.start`, error)}
                        </Typography>
                      )}
                    </Box>
                  )}
                />

                {/* End Time */}
                <Controller
                  name={`shifts.${index}.end`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box sx={{ flex: 1, position: "relative" }}>
                      <TextField
                        {...field}
                        size="small"
                        type="time"
                        error={!!error || !!formErrors[`shifts.${index}.end`]}
                        disabled={isLoading}
                        onBlur={field.onBlur}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            display: "flex",
                            height: "40px",
                            padding: "0 12px",
                            alignItems: "center",
                            gap: "6px",
                            borderRadius: "8px",
                            border: "1px solid rgba(9, 30, 66, 0.14)",
                            background: "rgba(9, 30, 66, 0.06)",
                            "& fieldset": { border: "none" },
                          },
                        }}
                      />
                      {field.value && (
                        <Typography
                          variant="body2"
                          sx={{
                            position: "absolute",
                            top: "20px",
                            right: 8,
                            transform: "translateY(-50%)",
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {getAmPm(field.value)}
                        </Typography>
                      )}
                      {(error || formErrors[`shifts.${index}.end`]) && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {getErrorMessage(`shifts.${index}.end`, error)}
                        </Typography>
                      )}
                    </Box>
                  )}
                />

                {fields.length > 1 && (
                  <IconButton
                    onClick={() => remove(index)}
                    size="small"
                    disabled={isLoading}
                    sx={{ color: "#f44336", mt: 1 }}
                  >
                    <DeleteIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}

        <Button
          startIcon={<AddIcon />}
          onClick={() => append({ start: "", end: "" })}
          disabled={isLoading}
          sx={{ mt: 1 }}
        >
          Add Shift
        </Button>
      </Box>

      {/* Lines */}
      <Box sx={{ mb: 4, width: { xs: "100%", sm: "25%" } }}>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: "var(--Text-color_text_secondary, #626F86)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Lines
        </Typography>
        <Controller
          name="lines"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl
              size="small"
              disabled={isLoading}
              error={!!error || !!formErrors.lines}
              sx={{ minWidth: 120 }}
            >
              <Select {...field} onBlur={field.onBlur}>
                <MenuItem value="">Select</MenuItem>
                {Array.from({ length: 30 }, (_, i) => {
                  const num = String(i + 1).padStart(2, "0");
                  return (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  );
                })}
              </Select>
              {(error || formErrors.lines) && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {getErrorMessage("lines", error)}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Box>
    </Box>
  );
};

export default PlantForm;
